import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";

export type OrganizationData = {
  id: string;
  name: string;
  created_at: string;
};

export type SortColumn = "name" | "created_at";
export type SortDirection = "asc" | "desc";

export function useOrganizationManagement() {
  const [organizations, setOrganizations] = useState<OrganizationData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<SortColumn>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const { toast } = useToast();
  const { subscriptionData } = useAuth();

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      console.log("Fetching organizations with subscription_id:", subscriptionData.subscription_id);
      
      let query = supabase
        .from("organizations")
        .select("id, name, created_at")
        .order(sortColumn, { ascending: sortDirection === "asc" });

      // Filtrar por subscription_id se fornecido
      if (subscriptionData.subscription_id) {
        query = query.eq("subscription_id", subscriptionData.subscription_id);
      }

      const { data, error } = await query;

      if (error) throw error;

      const filteredData = data?.map(org => ({
        id: org.id,
        name: org.name || "",
        created_at: org.created_at
      })) || [];

      setOrganizations(filteredData);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar organizações",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, [sortColumn, sortDirection, subscriptionData.subscription_id]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const createOrganization = async (name: string) => {
    try {
      const { error } = await supabase
        .from("organizations")
        .insert({
          name,
          subscription_id: subscriptionData.subscription_id
        });

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Organização criada com sucesso",
      });

      await fetchOrganizations();
      return true;
    } catch (error: any) {
      toast({
        title: "Erro ao criar organização",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const updateOrganization = async (id: string, name: string) => {
    try {
      const { error } = await supabase
        .from("organizations")
        .update({ name })
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Organização atualizada com sucesso",
      });

      await fetchOrganizations();
      return true;
    } catch (error: any) {
      toast({
        title: "Erro ao atualizar organização",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteOrganization = async (id: string) => {
    try {
      const { error } = await supabase
        .from("organizations")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Organização excluída com sucesso",
      });

      await fetchOrganizations();
      return true;
    } catch (error: any) {
      toast({
        title: "Erro ao excluir organização",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const filteredOrganizations = organizations.filter(org =>
    org.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return {
    organizations: filteredOrganizations,
    loading,
    searchTerm,
    setSearchTerm,
    sortColumn,
    sortDirection,
    onSort: handleSort,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    refreshOrganizations: fetchOrganizations,
  };
}
